<template>
  <main class="checkout-manage">
    <section class="checkout-manage__header">
      <a href="/checkout" class="checkout-manage__back">{{ $t('checkout.subscription.back') }}</a>
      <h1>{{ $t('checkout.subscription.title') }}</h1>
      <p>{{ visuals.name }}</p>
    </section>

    <section class="checkout-manage__panel">
      <template v-if="!authenticatedAsSubscriber">
        <h2>{{ $t('checkout.subscription.login_title') }}</h2>
        <p>{{ $t('checkout.subscription.login_text') }}</p>
        <SigninWithGoogle />
      </template>

      <template v-else-if="subscription">
        <div class="checkout-manage__amount">{{ subscription.level.price }} {{ subscription.level.currency }}</div>
        <div class="checkout-manage__meta">{{ $t('checkout.monthly') }}</div>
        <dl>
          <div>
            <dt>{{ $t('checkout.subscription.status') }}</dt>
            <dd>{{ $t('checkout.subscription.active') }}</dd>
          </div>
          <div v-if="subscription.paidTill">
            <dt>{{ $t('checkout.subscription.paid_until') }}</dt>
            <dd>{{ formatDate(subscription.paidTill) }}</dd>
          </div>
          <div v-if="subscription.nextRecurrentPayment && subscription.recurrent">
            <dt>{{ $t('checkout.subscription.next_charge') }}</dt>
            <dd>{{ formatDate(subscription.nextRecurrentPayment) }}</dd>
          </div>
        </dl>
        <button class="checkout-manage__danger" type="button" :disabled="inProgress" @click="cancel">
          {{ inProgress ? $t('checkout.subscription.canceling') : $t('checkout.subscription.cancel_monthly') }}
        </button>
      </template>

      <template v-else-if="rawSubscription && rawSubscription.inPayment">
        <h2>{{ $t('checkout.subscription.processing_title') }}</h2>
        <p>{{ $t('checkout.subscription.processing_text') }}</p>
        <button class="checkout-manage__primary" type="button" @click="$store.dispatch('updateSubscription')">{{ $t('checkout.subscription.refresh_status') }}</button>
      </template>

      <template v-else>
        <h2>{{ $t('checkout.subscription.not_found_title') }}</h2>
        <p>{{ $t('checkout.subscription.not_found_text') }}</p>
        <a class="checkout-manage__primary" href="/checkout">{{ $t('checkout.subscription.start_payment') }}</a>
      </template>

      <div v-if="errorMessage" class="checkout-manage__error">{{ errorMessage }}</div>
    </section>
  </main>
</template>

<script>
import {Unsubscribe} from '@/api/api';
import SigninWithGoogle from '@/components/general/SigninWithGoogle.vue';

export default {
  components: {SigninWithGoogle},
  data() {
    return {
      inProgress: false,
      localError: '',
    };
  },
  computed: {
    visuals() {
      return this.$store.getters.visuals || {};
    },
    authenticatedAsSubscriber() {
      return this.$store.getters.authenticatedAsSubscriber;
    },
    subscription() {
      const subscription = this.$store.getters.currentSubscription;
      return subscription && subscription.recurrent ? subscription : null;
    },
    rawSubscription() {
      return this.$store.state.currentSubscription;
    },
    errorMessage() {
      return this.localError || this.$store.getters.errorMessage;
    },
  },
  created() {
    if (this.authenticatedAsSubscriber) {
      this.$store.dispatch('updateSubscription');
    }
  },
  watch: {
    authenticatedAsSubscriber(value) {
      if (value) {
        this.$store.dispatch('updateSubscription');
      }
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleDateString(this.$i18n.locale || 'ru', {year: 'numeric', month: 'long', day: 'numeric'});
    },
    cancel() {
      this.localError = '';
      this.inProgress = true;
      Unsubscribe().then((response) => {
        this.$store.dispatch('updateCurrentSubscription', response.data?.subscription);
      }).catch((error) => {
        this.localError = error.response?.data?.error_text || this.$t('checkout.subscription.cancel_error');
      }).finally(() => {
        this.inProgress = false;
      });
    },
  },
};
</script>

<style scoped>
.checkout-manage {
  min-height: 100vh;
  background: #f5f7f8;
  color: #17212b;
  padding: 38px 20px;
}
.checkout-manage__header,
.checkout-manage__panel {
  max-width: 720px;
  margin: 0 auto;
}
.checkout-manage__back {
  color: var(--control-color, #1f6feb);
}
.checkout-manage__header h1 {
  margin: 24px 0 8px;
  font-size: 36px;
  letter-spacing: 0;
}
.checkout-manage__header p {
  margin: 0 0 24px;
  color: #66737f;
}
.checkout-manage__panel {
  background: #fff;
  border: 1px solid #dde3e8;
  border-radius: 8px;
  padding: 28px;
  box-shadow: 0 16px 40px rgba(25, 39, 52, .08);
}
.checkout-manage__panel h2 {
  margin: 0 0 10px;
  font-size: 24px;
  letter-spacing: 0;
}
.checkout-manage__panel p {
  color: #4c5a66;
}
.checkout-manage__amount {
  font-size: 42px;
  font-weight: 700;
}
.checkout-manage__meta {
  color: #66737f;
  margin-bottom: 22px;
}
.checkout-manage dl {
  margin: 0 0 22px;
}
.checkout-manage dl div {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid #edf1f4;
}
.checkout-manage dt {
  color: #66737f;
}
.checkout-manage dd {
  margin: 0;
}
.checkout-manage__primary,
.checkout-manage__danger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  border-radius: 6px;
  padding: 0 16px;
  text-decoration: none;
  border: 0;
  cursor: pointer;
}
.checkout-manage__primary {
  background: var(--control-color, #16794c);
  color: #fff;
}
.checkout-manage__panel p + .checkout-manage__primary {
  margin-top: 12px;
}
.checkout-manage__danger {
  background: #b42318;
  color: #fff;
}
.checkout-manage__danger:disabled {
  background: #98a5b1;
}
.checkout-manage__error {
  margin-top: 18px;
  color: #b42318;
}
@media (max-width: 620px) {
  .checkout-manage__header h1 {
    font-size: 30px;
  }
  .checkout-manage dl div {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
